// API calls for Connect: the Contacts tab and the Connect / Classic CGS Command Center views.
// See helpers/connect/connectFeatures.js for the flag and where each piece of data comes from.
//
// Its own module, like insights.service.js, rather than more methods on api.service.js.
//
// Every call resolves with the API envelope's `data` and throws a plain Error carrying the API's
// own message when the call fails (non-2xx, or isSuccess false), so screens can show it as is.
import axios from 'axios'
import auth from '@/authConfig.js'

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT
const CONTACTS = 'api/GSDesignContacts'

// "View as": the header's impersonation picker stores the person in localStorage imp_azureid ('' when off).
// Sent as X-GSD-Act-As, which the server checks with GSDesign's impersonation rules (Impersonation role and
// managers reach only people below them; admins anyone). If the signed-in user can't view as that person the
// server answers 403 with the reason; it never quietly falls back to the signed-in user's own data.
function viewingAs() {
  try { return (localStorage.getItem('imp_azureid') || '').trim() } catch (e) { return '' }
}

async function headers() {
  const tokenResponse = await auth.acquireTokenResponse()
  const token = tokenResponse?.accessToken
  const h = token ? { Authorization: 'Bearer ' + token } : {}
  const actAs = viewingAs()
  if (actAs) h['X-GSD-Act-As'] = actAs
  return h
}

function failure(err) {
  const body = err?.response?.data
  const msg = body?.message || body?.Message || (Array.isArray(body?.errors) && body.errors[0]) || err?.message || 'Please try again'
  const e = new Error(msg)
  e.status = err?.response?.status
  e.cause = err
  return e
}

// ServiceResponse comes back as { isSuccess, data, message } (GSWebAPIv2's naming policy only
// lowers the first letter). Older code paths send issuccess, so both are read.
function unwrap(body) {
  if (body && typeof body === 'object' && ('isSuccess' in body || 'issuccess' in body)) {
    const ok = body.isSuccess ?? body.issuccess
    if (ok === false) throw new Error(body.message || 'Please try again')
    return body.data
  }
  return body
}

async function get(path, params) {
  try {
    const res = await axios.get(`${API_ENDPOINT}${path}`, { headers: await headers(), params })
    return unwrap(res.data)
  } catch (err) {
    throw err?.response ? failure(err) : err
  }
}

async function post(path, body, params) {
  try {
    const res = await axios.post(`${API_ENDPOINT}${path}`, body ?? {}, { headers: await headers(), params })
    return unwrap(res.data)
  } catch (err) {
    throw err?.response ? failure(err) : err
  }
}

function actingAzureId() {
  try { return localStorage.getItem('azure_id') || '' } catch (e) { return '' }
}

// Options rarely changes (types, statuses, saved lists), so it's shared for a few minutes.
const OPTIONS_TTL_MS = 5 * 60 * 1000
let optionsCache = null

export default {
  // ── Contacts (GSDesignContacts) ──────────────────────────────────────────────────────
  /** → { types[], statuses[], tags[], fields[], saved_lists[], team[] … } */
  contactOptions({ fresh = false } = {}) {
    const who = viewingAs() // a different person's options after switching "view as"
    if (!fresh && optionsCache && optionsCache.who === who && Date.now() - optionsCache.at < OPTIONS_TTL_MS) return optionsCache.promise
    const promise = get(`${CONTACTS}/Options`)
    optionsCache = { at: Date.now(), who, promise }
    promise.catch(() => { optionsCache = null })
    return promise
  },

  /**
   * { list: 'all'|'followed'|'leads'|'clients'|'agents'|'birthdays'|'saved:<title>', scope: 'mine'|'team'|'branch',
   *   search, filters: [{ field, op, value }], sort, sort_dir, page, page_size } → { rows[], total }
   */
  queryContacts(q) {
    return post(`${CONTACTS}/Query`, q)
  },

  /** → { contact, can_edit, notes[], tasks[], connected[], loans[], possible_duplicates[], timeline[] } */
  getContact(id) {
    return get(`${CONTACTS}/Get`, { id })
  },

  /** DialPad calls / texts / emails for the timeline. Loaded after getContact so the page shows first. */
  communications(id) {
    return get(`${CONTACTS}/Communications`, { id })
  },

  /** Edits a contact. Send the whole contact from getContact with the change applied. */
  saveContact(contact) {
    return post(`${CONTACTS}/Save`, contact)
  },

  /**
   * Replaces ALL of the caller's saved lists (the CGS2022 "My saved lists", shared with CGS and
   * GSDesign): [{ title, filters: [{ field, op, value }], is_default }] → the saved lists.
   * Send every list you want to keep, not just the changed one.
   */
  async saveLists(lists) {
    optionsCache = null // saved_lists ride along in Options
    const saved = await post(`${CONTACTS}/SaveLists`, lists)
    optionsCache = null
    return saved
  },

  /** Ordered column keys for the contacts list (shared with CGS2022's saved grid columns) → saved keys */
  async saveColumns(keys) {
    const saved = await post(`${CONTACTS}/SaveColumns`, keys)
    optionsCache = null // column_layout rides along in Options
    return saved
  },

  addNote(contactId, note) {
    return post(`${CONTACTS}/AddNote`, { contact_id: contactId, note })
  },

  /** task: { task_id?, subject, due_date, status, priority, comments } */
  saveTask(contactId, task) {
    return post(`${CONTACTS}/SaveTask`, { contact_id: contactId, task })
  },

  // ── Pipeline Board (GSWebAPIv2 PipelineBoardController) ──────────────────────────────
  /** → { fields: [{ key, label, kind, group, sortable, choices? }], scope }. 404 until the API is deployed. */
  pipelineBoardOptions() {
    return get('api/PipelineBoard/Options')
  },

  /** Pick-list values for one choice field (e.g. 'milestone') from the caller's loans → string[]. 404 on an older API. */
  pipelineBoardChoices(field) {
    return get('api/PipelineBoard/Choices', { field })
  },

  /** { filters: [{ field, op, value }], search?, sort?, sort_dir?, page, page_size } → { rows[], total, volume } */
  pipelineBoardQuery(q) {
    return post('api/PipelineBoard/Query', q)
  },

  /** Saved columns per board, from the account: { prospect?: [{ id, title, filters }], loans?: [...] }. A board never saved is absent. */
  getBoards() {
    return get('api/PipelineBoard/Boards')
  },

  /** Replaces one board's saved columns ('prospect' | 'loans') → the saved columns. Refused while viewing as someone. */
  saveBoard(board, columns) {
    return post('api/PipelineBoard/SaveBoard', { board, columns })
  },

  // ── CGS2022 dashboard cards ─────────────────────────────────────────────────────────
  /** CGS2022 tasksCard.vue: the acting user's tasks. status 'Completed' for the done list. */
  getTasks(status) {
    return get('api/CGSTask/GetTasks', { azureId: actingAzureId(), ...(status ? { status } : {}) })
  },

  /** CGS2022 birthdayCard.vue */
  getImminentBirthdays() {
    return get('api/Birthdays/GetImminentBirthdays', { azureId: actingAzureId() })
  },

  acknowledgeBirthday(guid) {
    return post('api/Birthdays/AcknowledgeBirthday', {}, { Guid: guid })
  },

  actingAzureId,
  viewingAs
}
